import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { LogOut, Plus, Pencil, Trash2, Package, Settings, ShoppingBag, ClipboardList, Home, Eye, EyeOff, GripVertical, ArrowUp, ArrowDown, Users, Phone, CalendarDays, User, KeyRound } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useAdmin() {
  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };
  return { token, headers };
}

/* ───── Property Tab ───── */
function PropertyTab() {
  const { headers } = useAdmin();
  const [form, setForm] = useState({ name: '', address: '', description: '', image_url: '', check_in_time: '', check_out_time: '', wifi_network: '', wifi_password: '', host_phone: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/property`, { headers }).then(r => {
      if (r.data && r.data.name) setForm(r.data);
    }).catch(console.error);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/property`, form, { headers });
      toast.success('Property updated');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const Field = ({ label, field, type = 'text', placeholder = '' }) => (
    <div>
      <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">{label}</label>
      <Input data-testid={`property-${field}-input`} type={type} placeholder={placeholder} value={form[field] || ''} onChange={e => setForm({ ...form, [field]: e.target.value })} className="bg-white border-warm-border rounded-xl" />
    </div>
  );

  return (
    <div className="space-y-5">
      <h2 className="font-heading text-lg text-espresso">Property Details</h2>
      <div className="bg-white border border-warm-border rounded-2xl p-5 space-y-4">
        <Field label="Property Name" field="name" placeholder="My Property" />
        <Field label="Address" field="address" placeholder="123 Beach Dr" />
        <div>
          <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Description</label>
          <Textarea data-testid="property-description-input" placeholder="About this property..." value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-white border-warm-border rounded-xl" rows={3} />
        </div>
        <Field label="Property Image URL" field="image_url" placeholder="https://..." />
      </div>

      <div className="bg-white border border-warm-border rounded-2xl p-5 space-y-4">
        <p className="text-xs uppercase tracking-widest text-text-muted font-semibold">Check-In / Check-Out</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Check-In Time" field="check_in_time" placeholder="3:00 PM" />
          <Field label="Check-Out Time" field="check_out_time" placeholder="11:00 AM" />
        </div>
      </div>

      <div className="bg-white border border-warm-border rounded-2xl p-5 space-y-4">
        <p className="text-xs uppercase tracking-widest text-text-muted font-semibold">WiFi</p>
        <Field label="Network Name" field="wifi_network" placeholder="MyWiFi_5G" />
        <Field label="Password" field="wifi_password" placeholder="password123" />
      </div>

      <div className="bg-white border border-warm-border rounded-2xl p-5 space-y-4">
        <p className="text-xs uppercase tracking-widest text-text-muted font-semibold">Host Contact</p>
        <Field label="Phone (for order alerts)" field="host_phone" type="tel" placeholder="+15551234567" />
      </div>

      <Button data-testid="save-property-btn" onClick={save} disabled={saving} className="w-full bg-espresso text-white rounded-full h-12 font-medium">
        {saving ? 'Saving...' : 'Save Property'}
      </Button>
    </div>
  );
}

/* ───── Sections Tab ───── */
function SectionsTab() {
  const { headers } = useAdmin();
  const [sections, setSections] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', icon: '', description: '', content: '', image_url: '', video_url: '', order: 0, is_active: true });
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/sections`, { headers }).then(r => setSections(r.data)).catch(console.error);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(load, [load]);

  const save = async () => {
    try {
      const data = { ...form, order: parseInt(form.order) || 0 };
      if (editing) { await axios.put(`${API}/admin/sections/${editing}`, data, { headers }); toast.success('Section updated'); }
      else { await axios.post(`${API}/admin/sections`, data, { headers }); toast.success('Section created'); }
      setOpen(false); setEditing(null); load();
    } catch { toast.error('Failed to save'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this section?')) return;
    await axios.delete(`${API}/admin/sections/${id}`, { headers });
    toast.success('Deleted'); load();
  };

  const openEdit = (s) => {
    setEditing(s.id);
    setForm({ title: s.title, icon: s.icon || '', description: s.description || '', content: s.content || '', image_url: s.image_url || '', video_url: s.video_url || '', order: s.order || 0, is_active: s.is_active !== false, images: s.images || [], faqs: s.faqs || [] });
    setOpen(true);
  };

  const moveSection = async (id, direction) => {
    const idx = sections.findIndex(s => s.id === id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sections.length) return;
    const a = sections[idx], b = sections[swapIdx];
    await Promise.all([
      axios.put(`${API}/admin/sections/${a.id}`, { ...a, order: b.order }, { headers }),
      axios.put(`${API}/admin/sections/${b.id}`, { ...b, order: a.order }, { headers }),
    ]);
    load();
  };

  const toggleActive = async (s) => {
    await axios.put(`${API}/admin/sections/${s.id}`, { ...s, is_active: !s.is_active }, { headers });
    toast.success(s.is_active ? 'Section hidden' : 'Section visible');
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg text-espresso">Property Guide ({sections.length})</h2>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-section-btn" size="sm" onClick={() => { setEditing(null); setForm({ title: '', icon: '', description: '', content: '', image_url: '', video_url: '', order: sections.length, is_active: true }); }} className="bg-espresso text-white rounded-full text-xs">
              <Plus size={14} className="mr-1" /> Add Section
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit' : 'New'} Section</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Title</label>
                <Input data-testid="section-title-input" placeholder="e.g., WiFi Information" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="bg-white border-warm-border rounded-xl" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Icon Name</label>
                <Input placeholder="e.g., Wifi, Lock, Tv, Trash2" value={form.icon} onChange={e => setForm({ ...form, icon: e.target.value })} className="bg-white border-warm-border rounded-xl" />
                <p className="text-[10px] text-text-muted mt-1">Lucide icon name: Wifi, Trash2, Gamepad2, Tv, Lock, Thermometer, ShieldAlert, MapPin, LogOut</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Short Description</label>
                <Textarea placeholder="Brief summary shown on card" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-white border-warm-border rounded-xl" rows={2} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Full Content (HTML)</label>
                <Textarea placeholder="<h3>Heading</h3><p>Details...</p>" value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} className="bg-white border-warm-border rounded-xl font-mono text-xs" rows={6} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Image URL</label>
                <Input placeholder="https://images.unsplash.com/..." value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} className="bg-white border-warm-border rounded-xl" />
                {form.image_url && <img src={form.image_url} alt="" className="mt-2 w-full h-32 object-cover rounded-xl" />}
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Video URL (YouTube embed)</label>
                <Input placeholder="https://www.youtube.com/embed/..." value={form.video_url} onChange={e => setForm({ ...form, video_url: e.target.value })} className="bg-white border-warm-border rounded-xl" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Display Order</label>
                <Input type="number" value={form.order} onChange={e => setForm({ ...form, order: e.target.value })} className="bg-white border-warm-border rounded-xl w-24" />
              </div>
              <div className="flex items-center justify-between bg-sand rounded-xl p-3">
                <span className="text-sm text-espresso font-medium">Visible to guests</span>
                <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} />
              </div>
              <Button data-testid="save-section-btn" onClick={save} className="w-full bg-espresso text-white rounded-full h-11">{editing ? 'Update Section' : 'Create Section'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-2">
        {sections.map((s, idx) => (
          <div key={s.id} data-testid={`admin-section-${s.id}`} className={`bg-white border border-warm-border rounded-xl p-4 flex items-center gap-3 ${!s.is_active ? 'opacity-50' : ''}`}>
            <div className="flex flex-col gap-0.5">
              <button onClick={() => moveSection(s.id, 'up')} disabled={idx === 0} className="text-text-muted hover:text-espresso disabled:opacity-20 p-0.5"><ArrowUp size={12} /></button>
              <button onClick={() => moveSection(s.id, 'down')} disabled={idx === sections.length - 1} className="text-text-muted hover:text-espresso disabled:opacity-20 p-0.5"><ArrowDown size={12} /></button>
            </div>
            {s.image_url && <img src={s.image_url} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-espresso truncate">{s.title}</p>
              <p className="text-xs text-text-muted truncate">{s.description}</p>
            </div>
            <button onClick={() => toggleActive(s)} className="text-text-muted hover:text-espresso p-1" title={s.is_active ? 'Hide' : 'Show'}>
              {s.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
            </button>
            <button onClick={() => openEdit(s)} className="text-text-muted hover:text-espresso p-1"><Pencil size={14} /></button>
            <button onClick={() => remove(s.id)} className="text-text-muted hover:text-warm-error p-1"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───── Services Tab ───── */
function ServicesTab() {
  const { headers } = useAdmin();
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', price: 0, image_url: '', category: '', is_active: true, requires_date: false, requires_time: false });
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/services`, { headers }).then(r => setServices(r.data)).catch(console.error);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(load, [load]);

  const save = async () => {
    try {
      const data = { ...form, price: parseFloat(form.price) || 0 };
      if (editing) { await axios.put(`${API}/admin/services/${editing}`, data, { headers }); }
      else { await axios.post(`${API}/admin/services`, data, { headers }); }
      toast.success('Saved'); setOpen(false); setEditing(null); load();
    } catch { toast.error('Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    await axios.delete(`${API}/admin/services/${id}`, { headers });
    toast.success('Deleted'); load();
  };

  const toggleActive = async (s) => {
    await axios.put(`${API}/admin/services/${s.id}`, { ...s, is_active: !s.is_active }, { headers });
    toast.success(s.is_active ? 'Service disabled' : 'Service enabled');
    load();
  };

  const openEdit = (s) => {
    setEditing(s.id);
    setForm({ title: s.title, description: s.description || '', price: s.price || 0, image_url: s.image_url || '', category: s.category || '', is_active: s.is_active !== false, requires_date: !!s.requires_date, requires_time: !!s.requires_time });
    setOpen(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg text-espresso">Services ({services.length})</h2>
        <Dialog open={open} onOpenChange={v => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-service-btn" size="sm" onClick={() => { setEditing(null); setForm({ title: '', description: '', price: 0, image_url: '', category: '', is_active: true, requires_date: false, requires_time: false }); }} className="bg-espresso text-white rounded-full text-xs">
              <Plus size={14} className="mr-1" /> Add Service
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit' : 'New'} Service</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Service Name</label>
                <Input data-testid="service-title-input" placeholder="e.g., Private Chef" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="bg-white border-warm-border rounded-xl" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Description</label>
                <Textarea placeholder="What's included..." value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-white border-warm-border rounded-xl" rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Price ($)</label>
                  <Input data-testid="service-price-input" type="number" step="0.01" placeholder="0.00" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} className="bg-white border-warm-border rounded-xl" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Category</label>
                  <Input placeholder="e.g., Dining" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="bg-white border-warm-border rounded-xl" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Image URL</label>
                <Input placeholder="https://..." value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} className="bg-white border-warm-border rounded-xl" />
                {form.image_url && <img src={form.image_url} alt="" className="mt-2 w-full h-32 object-cover rounded-xl" />}
              </div>
              <div className="space-y-3 bg-sand rounded-xl p-4">
                <p className="text-xs uppercase tracking-widest text-text-muted font-semibold">Scheduling Options</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-espresso">Requires date selection</span>
                  <Switch checked={form.requires_date} onCheckedChange={v => setForm({ ...form, requires_date: v })} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-espresso">Requires time selection</span>
                  <Switch checked={form.requires_time} onCheckedChange={v => setForm({ ...form, requires_time: v })} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-espresso">Visible to guests</span>
                  <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} />
                </div>
              </div>
              <Button data-testid="save-service-btn" onClick={save} className="w-full bg-espresso text-white rounded-full h-11">{editing ? 'Update Service' : 'Create Service'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-2">
        {services.map(s => (
          <div key={s.id} data-testid={`admin-service-${s.id}`} className={`bg-white border border-warm-border rounded-xl p-4 flex items-center gap-3 ${!s.is_active ? 'opacity-50' : ''}`}>
            {s.image_url && <img src={s.image_url} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-espresso truncate">{s.title}</p>
              <p className="text-xs text-text-muted">${s.price?.toFixed(2)} &middot; {s.category}</p>
            </div>
            <button onClick={() => toggleActive(s)} className="text-text-muted hover:text-espresso p-1">
              {s.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
            </button>
            <button onClick={() => openEdit(s)} className="text-text-muted hover:text-espresso p-1"><Pencil size={14} /></button>
            <button onClick={() => remove(s.id)} className="text-text-muted hover:text-warm-error p-1"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───── Products Tab ───── */
function ProductsTab() {
  const { headers } = useAdmin();
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', price: 0, image_url: '', inventory: 0, shipping_available: false, category: '', is_active: true });
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/products`, { headers }).then(r => setProducts(r.data)).catch(console.error);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(load, [load]);

  const save = async () => {
    try {
      const data = { ...form, price: parseFloat(form.price) || 0, inventory: parseInt(form.inventory) || 0 };
      if (editing) { await axios.put(`${API}/admin/products/${editing}`, data, { headers }); }
      else { await axios.post(`${API}/admin/products`, data, { headers }); }
      toast.success('Saved'); setOpen(false); setEditing(null); load();
    } catch { toast.error('Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await axios.delete(`${API}/admin/products/${id}`, { headers });
    toast.success('Deleted'); load();
  };

  const toggleActive = async (p) => {
    await axios.put(`${API}/admin/products/${p.id}`, { ...p, is_active: !p.is_active }, { headers });
    toast.success(p.is_active ? 'Product hidden' : 'Product visible');
    load();
  };

  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ title: p.title, description: p.description || '', price: p.price || 0, image_url: p.image_url || '', inventory: p.inventory || 0, shipping_available: !!p.shipping_available, category: p.category || '', is_active: p.is_active !== false });
    setOpen(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg text-espresso">Products ({products.length})</h2>
        <Dialog open={open} onOpenChange={v => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-product-btn" size="sm" onClick={() => { setEditing(null); setForm({ title: '', description: '', price: 0, image_url: '', inventory: 0, shipping_available: false, category: '', is_active: true }); }} className="bg-espresso text-white rounded-full text-xs">
              <Plus size={14} className="mr-1" /> Add Product
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit' : 'New'} Product</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Product Name</label>
                <Input data-testid="product-title-input" placeholder="e.g., Artisan Soy Candle" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="bg-white border-warm-border rounded-xl" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Description</label>
                <Textarea placeholder="Product details..." value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-white border-warm-border rounded-xl" rows={3} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Price ($)</label>
                  <Input data-testid="product-price-input" type="number" step="0.01" placeholder="0.00" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} className="bg-white border-warm-border rounded-xl" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Inventory</label>
                  <Input data-testid="product-inventory-input" type="number" placeholder="0" value={form.inventory} onChange={e => setForm({ ...form, inventory: e.target.value })} className="bg-white border-warm-border rounded-xl" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Category</label>
                  <Input placeholder="Bar" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="bg-white border-warm-border rounded-xl" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Image URL</label>
                <Input placeholder="https://..." value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} className="bg-white border-warm-border rounded-xl" />
                {form.image_url && <img src={form.image_url} alt="" className="mt-2 w-full h-32 object-cover rounded-xl" />}
              </div>
              <div className="space-y-3 bg-sand rounded-xl p-4">
                <p className="text-xs uppercase tracking-widest text-text-muted font-semibold">Options</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-espresso">Shipping available</span>
                  <Switch checked={form.shipping_available} onCheckedChange={v => setForm({ ...form, shipping_available: v })} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-espresso">Visible to guests</span>
                  <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} />
                </div>
              </div>
              <Button data-testid="save-product-btn" onClick={save} className="w-full bg-espresso text-white rounded-full h-11">{editing ? 'Update Product' : 'Create Product'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-2">
        {products.map(p => (
          <div key={p.id} data-testid={`admin-product-${p.id}`} className={`bg-white border border-warm-border rounded-xl p-4 flex items-center gap-3 ${!p.is_active ? 'opacity-50' : ''}`}>
            {p.image_url && <img src={p.image_url} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-espresso truncate">{p.title}</p>
              <p className="text-xs text-text-muted">${p.price?.toFixed(2)} &middot; Stock: {p.inventory}</p>
            </div>
            <button onClick={() => toggleActive(p)} className="text-text-muted hover:text-espresso p-1">
              {p.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
            </button>
            <button onClick={() => openEdit(p)} className="text-text-muted hover:text-espresso p-1"><Pencil size={14} /></button>
            <button onClick={() => remove(p.id)} className="text-text-muted hover:text-warm-error p-1"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───── Orders Tab ───── */
function OrdersTab() {
  const { headers } = useAdmin();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('all');

  const load = useCallback(() => {
    axios.get(`${API}/admin/orders`, { headers }).then(r => setOrders(r.data)).catch(console.error);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(load, [load]);

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/admin/orders/${id}`, { status }, { headers });
    toast.success('Status updated');
    load();
  };

  const statusColors = {
    pending: 'bg-warmgold/10 text-warmgold',
    confirmed: 'bg-sage/10 text-sage',
    fulfilled: 'bg-sage/10 text-sage',
    cancelled: 'bg-warm-error/10 text-warm-error',
  };

  const filtered = filter === 'all' ? orders : orders.filter(o => o.status === filter);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg text-espresso">Orders ({orders.length})</h2>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="order-filter" className="w-32 h-8 text-xs rounded-lg">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="fulfilled">Fulfilled</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {filtered.length === 0 ? (
        <p className="text-text-muted text-sm text-center py-8">No orders found</p>
      ) : (
        <div className="space-y-3">
          {filtered.map(o => (
            <div key={o.id} data-testid={`admin-order-${o.id}`} className="bg-white border border-warm-border rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-mono text-text-muted">#{o.id.slice(0, 8)}</span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColors[o.status] || ''}`}>{o.status}</span>
              </div>
              {o.phone && <p className="text-xs text-text-muted mb-2">Guest: {o.phone}</p>}
              <div className="mb-2">
                {o.items?.map((item, i) => (
                  <p key={i} className="text-xs text-text-muted">{item.title} x{item.quantity} — ${item.subtotal?.toFixed(2)}</p>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-text-muted">{new Date(o.created_at).toLocaleString()}</span>
                <span className="text-sm font-bold text-espresso">${o.total?.toFixed(2)}</span>
              </div>
              <div className="flex gap-2 mt-3">
                <Select onValueChange={(v) => updateStatus(o.id, v)} defaultValue={o.status}>
                  <SelectTrigger data-testid={`order-status-select-${o.id}`} className="h-8 text-xs rounded-lg flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="fulfilled">Fulfilled</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ───── Guests Tab ───── */
function GuestsTab() {
  const { headers } = useAdmin();
  const [guests, setGuests] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ phone: '', guest_name: '', check_in: '', check_out: '', notes: '' });
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/guests`, { headers }).then(r => setGuests(r.data)).catch(console.error);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(load, [load]);

  const save = async () => {
    if (!form.phone) { toast.error('Phone number is required'); return; }
    if (!form.check_in || !form.check_out) { toast.error('Check-in and check-out dates are required'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/admin/guests/${editing}`, form, { headers });
        toast.success('Guest updated');
      } else {
        await axios.post(`${API}/admin/guests`, form, { headers });
        toast.success('Guest added');
      }
      setOpen(false); setEditing(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this guest?')) return;
    await axios.delete(`${API}/admin/guests/${id}`, { headers });
    toast.success('Guest removed'); load();
  };

  const openEdit = (g) => {
    setEditing(g.id);
    setForm({ phone: g.phone || '', guest_name: g.guest_name || '', check_in: g.check_in || '', check_out: g.check_out || '', notes: g.notes || '' });
    setOpen(true);
  };

  const today = new Date().toISOString().split('T')[0];
  const activeGuests = guests.filter(g => g.check_out >= today);
  const pastGuests = guests.filter(g => g.check_out < today);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg text-espresso">Guests ({guests.length})</h2>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-guest-btn" size="sm" onClick={() => { setEditing(null); setForm({ phone: '', guest_name: '', check_in: '', check_out: '', notes: '' }); }} className="bg-espresso text-white rounded-full text-xs">
              <Plus size={14} className="mr-1" /> Add Guest
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit' : 'Add'} Guest</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Guest Name</label>
                <Input data-testid="guest-name-input" placeholder="John Smith" value={form.guest_name} onChange={e => setForm({ ...form, guest_name: e.target.value })} className="bg-white border-warm-border rounded-xl" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Mobile Phone Number</label>
                <Input data-testid="guest-phone-input" type="tel" placeholder="+1 (555) 123-4567" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-white border-warm-border rounded-xl" />
                <p className="text-[10px] text-text-muted mt-1">Include country code. This is the number they'll use to log in.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Check-In Date</label>
                  <Input data-testid="guest-checkin-input" type="date" value={form.check_in} onChange={e => setForm({ ...form, check_in: e.target.value })} className="bg-white border-warm-border rounded-xl" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Check-Out Date</label>
                  <Input data-testid="guest-checkout-input" type="date" value={form.check_out} onChange={e => setForm({ ...form, check_out: e.target.value })} className="bg-white border-warm-border rounded-xl" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Notes (optional)</label>
                <Textarea placeholder="VIP guest, late arrival, etc." value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="bg-white border-warm-border rounded-xl" rows={2} />
              </div>
              <Button data-testid="save-guest-btn" onClick={save} className="w-full bg-espresso text-white rounded-full h-11">{editing ? 'Update Guest' : 'Add Guest'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Active / Current Guests */}
      {activeGuests.length > 0 && (
        <div className="mb-6">
          <p className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-3">Current & Upcoming</p>
          <div className="space-y-2">
            {activeGuests.map(g => (
              <div key={g.id} data-testid={`admin-guest-${g.id}`} className="bg-white border border-warm-border rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-sand rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Users size={16} className="text-espresso" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-espresso">{g.guest_name || 'Guest'}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <Phone size={11} className="text-text-muted" />
                      <p className="text-xs text-text-muted">{g.phone}</p>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <CalendarDays size={11} className="text-text-muted" />
                      <p className="text-xs text-text-muted">{g.check_in} to {g.check_out}</p>
                    </div>
                    {g.notes && <p className="text-xs text-text-muted/70 mt-1 italic">{g.notes}</p>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(g)} className="text-text-muted hover:text-espresso p-1"><Pencil size={14} /></button>
                    <button onClick={() => remove(g.id)} className="text-text-muted hover:text-warm-error p-1"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Past Guests */}
      {pastGuests.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-3">Past Guests</p>
          <div className="space-y-2">
            {pastGuests.map(g => (
              <div key={g.id} data-testid={`admin-guest-past-${g.id}`} className="bg-white border border-warm-border rounded-xl p-4 opacity-60">
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-espresso">{g.guest_name || 'Guest'} &middot; <span className="text-text-muted text-xs">{g.phone}</span></p>
                    <p className="text-xs text-text-muted">{g.check_in} to {g.check_out}</p>
                  </div>
                  <button onClick={() => remove(g.id)} className="text-text-muted hover:text-warm-error p-1"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {guests.length === 0 && (
        <div className="text-center py-12">
          <div className="w-14 h-14 bg-sand rounded-full flex items-center justify-center mx-auto mb-3">
            <Users size={24} className="text-text-muted" />
          </div>
          <p className="text-text-muted text-sm mb-1">No guests registered yet</p>
          <p className="text-text-muted/60 text-xs">Add guest phone numbers so they can log in to your property portal</p>
        </div>
      )}
    </div>
  );
}

/* ───── Account Tab ───── */
function AccountTab() {
  const { headers } = useAdmin();
  const [profile, setProfile] = useState({ email: '', name: '' });
  const [emailForm, setEmailForm] = useState({ new_email: '', current_password: '' });
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [emailSaving, setEmailSaving] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/profile`, { headers }).then(r => {
      setProfile(r.data);
      setEmailForm(f => ({ ...f, new_email: r.data.email }));
    }).catch(console.error);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateEmail = async () => {
    if (!emailForm.new_email || !emailForm.current_password) { toast.error('Please fill in all fields'); return; }
    setEmailSaving(true);
    try {
      await axios.put(`${API}/admin/profile/email`, emailForm, { headers });
      toast.success('Email updated successfully');
      setProfile(p => ({ ...p, email: emailForm.new_email }));
      setEmailForm(f => ({ ...f, current_password: '' }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update email');
    } finally { setEmailSaving(false); }
  };

  const updatePassword = async () => {
    if (!pwForm.current_password || !pwForm.new_password) { toast.error('Please fill in all fields'); return; }
    if (pwForm.new_password !== pwForm.confirm_password) { toast.error('New passwords do not match'); return; }
    if (pwForm.new_password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setPwSaving(true);
    try {
      await axios.put(`${API}/admin/profile/password`, { current_password: pwForm.current_password, new_password: pwForm.new_password }, { headers });
      toast.success('Password updated successfully');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update password');
    } finally { setPwSaving(false); }
  };

  return (
    <div className="space-y-6">
      <h2 className="font-heading text-lg text-espresso">Account Settings</h2>

      <div className="bg-white border border-warm-border rounded-2xl p-5 space-y-4">
        <p className="text-xs uppercase tracking-widest text-text-muted font-semibold">Update Email</p>
        <div>
          <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">New Email</label>
          <Input data-testid="account-email-input" type="email" value={emailForm.new_email} onChange={e => setEmailForm({ ...emailForm, new_email: e.target.value })} className="bg-white border-warm-border rounded-xl" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Current Password</label>
          <Input data-testid="account-email-password" type="password" placeholder="Enter current password to confirm" value={emailForm.current_password} onChange={e => setEmailForm({ ...emailForm, current_password: e.target.value })} className="bg-white border-warm-border rounded-xl" />
        </div>
        <Button data-testid="save-email-btn" onClick={updateEmail} disabled={emailSaving} className="w-full bg-espresso text-white rounded-full h-11">
          {emailSaving ? 'Saving...' : 'Update Email'}
        </Button>
      </div>

      <div className="bg-white border border-warm-border rounded-2xl p-5 space-y-4">
        <p className="text-xs uppercase tracking-widest text-text-muted font-semibold">Change Password</p>
        <div>
          <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Current Password</label>
          <Input data-testid="account-current-password" type="password" placeholder="Enter current password" value={pwForm.current_password} onChange={e => setPwForm({ ...pwForm, current_password: e.target.value })} className="bg-white border-warm-border rounded-xl" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">New Password</label>
          <Input data-testid="account-new-password" type="password" placeholder="Minimum 6 characters" value={pwForm.new_password} onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })} className="bg-white border-warm-border rounded-xl" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-1.5 block">Confirm New Password</label>
          <Input data-testid="account-confirm-password" type="password" placeholder="Re-enter new password" value={pwForm.confirm_password} onChange={e => setPwForm({ ...pwForm, confirm_password: e.target.value })} className="bg-white border-warm-border rounded-xl" />
        </div>
        <Button data-testid="save-password-btn" onClick={updatePassword} disabled={pwSaving} className="w-full bg-espresso text-white rounded-full h-11">
          {pwSaving ? 'Saving...' : 'Update Password'}
        </Button>
      </div>
    </div>
  );
}

/* ───── Main Dashboard ───── */
export default function AdminDashboard() {
  const navigate = useNavigate();
  const { token } = useAdmin();

  useEffect(() => {
    if (!token) navigate('/admin/login');
  }, [token, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-bone">
      <div className="max-w-2xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 data-testid="admin-dashboard-title" className="font-heading text-2xl text-espresso">Owner Dashboard</h1>
            <p className="text-text-muted text-xs mt-0.5">Manage your property, services, and orders</p>
          </div>
          <Button data-testid="admin-logout-btn" variant="ghost" size="sm" onClick={handleLogout} className="text-text-muted">
            <LogOut size={16} className="mr-1" /> Logout
          </Button>
        </div>

        <Tabs defaultValue="guests">
          <TabsList data-testid="admin-tabs" className="w-full bg-sand rounded-xl h-auto min-h-[44px] mb-6 flex flex-wrap gap-1 p-1">
            <TabsTrigger value="guests" className="flex-1 rounded-lg text-xs data-[state=active]:bg-white py-2"><Users size={14} className="mr-1" /> Guests</TabsTrigger>
            <TabsTrigger value="property" className="flex-1 rounded-lg text-xs data-[state=active]:bg-white py-2"><Home size={14} className="mr-1" /> Property</TabsTrigger>
            <TabsTrigger value="sections" className="flex-1 rounded-lg text-xs data-[state=active]:bg-white py-2"><Settings size={14} className="mr-1" /> Guide</TabsTrigger>
            <TabsTrigger value="services" className="flex-1 rounded-lg text-xs data-[state=active]:bg-white py-2"><Package size={14} className="mr-1" /> Services</TabsTrigger>
            <TabsTrigger value="products" className="flex-1 rounded-lg text-xs data-[state=active]:bg-white py-2"><ShoppingBag size={14} className="mr-1" /> Shop</TabsTrigger>
            <TabsTrigger value="orders" className="flex-1 rounded-lg text-xs data-[state=active]:bg-white py-2"><ClipboardList size={14} className="mr-1" /> Orders</TabsTrigger>
            <TabsTrigger value="account" className="flex-1 rounded-lg text-xs data-[state=active]:bg-white py-2"><User size={14} className="mr-1" /> Account</TabsTrigger>
          </TabsList>

          <TabsContent value="guests"><GuestsTab /></TabsContent>
          <TabsContent value="property"><PropertyTab /></TabsContent>
          <TabsContent value="sections"><SectionsTab /></TabsContent>
          <TabsContent value="services"><ServicesTab /></TabsContent>
          <TabsContent value="products"><ProductsTab /></TabsContent>
          <TabsContent value="orders"><OrdersTab /></TabsContent>
          <TabsContent value="account"><AccountTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
